import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Users, Building2, Database, ChevronLeft, Shield, Circle, Sliders,
  ChevronRight, Settings, MessageSquare
} from 'lucide-react';
import UserManagement from './admin/UserManagement';
import DepartmentManagement from './admin/DepartmentManagement';
import DatabaseSettings from './admin/DatabaseSettings';
import StatusManagement from './admin/StatusManagement';
import GeneralSettings from './admin/GeneralSettings';
import PatientSettings from './admin/PatientSettings';

const Admin = ({ onBack, currentUser }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('general');
  const [menuOpen, setMenuOpen] = useState(true);

  const categories = [
    {
      id: 'cat-general',
      label: t('admin.categories.general'),
      icon: Settings,
      items: [
        { id: 'general', label: t('admin.tabs.general'), icon: Sliders },
        { id: 'databases', label: t('admin.tabs.databases'), icon: Database },
      ]
    },
    {
      id: 'cat-staff',
      label: t('admin.categories.staff'),
      icon: Users,
      items: [
        { id: 'users', label: t('admin.tabs.users'), icon: Users },
        { id: 'departments', label: t('admin.tabs.departments'), icon: Building2 },
        { id: 'statuses', label: t('admin.tabs.statuses'), icon: Circle },
      ]
    },
    {
      id: 'cat-patient',
      label: t('admin.categories.patient'),
      icon: MessageSquare,
      items: [
        { id: 'patientSettings', label: t('patient.adminSettings.generalTab'), icon: Settings },
      ]
    },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'general':
        return <GeneralSettings />;
      case 'users':
        return <UserManagement />;
      case 'departments':
        return <DepartmentManagement />;
      case 'statuses':
        return <StatusManagement />;
      case 'databases':
        return <DatabaseSettings />;
      case 'patientSettings':
        return <PatientSettings />;
      default:
        return <GeneralSettings />;
    }
  };

  // Find active category for highlight
  const activeCategoryId = categories.find(c =>
    c.items.some(item => item.id === activeTab)
  )?.id;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
        color: 'white',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            onClick={onBack}
            style={{
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '8px',
              padding: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ChevronLeft size={18} />
          </button>
          <Shield size={20} />
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>{t('admin.title')}</h1>
            <p style={{ fontSize: '11px', opacity: 0.8, margin: 0 }}>{t('admin.subtitle')}</p>
          </div>
          {/* Menu toggle for small panels */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '8px',
              padding: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title={menuOpen ? 'Menüyü gizle' : 'Menüyü göster'}
          >
            <ChevronRight size={16} style={{
              transform: menuOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s'
            }} />
          </button>
        </div>
      </div>

      {/* Body: Sidebar + Content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Sidebar Menu */}
        {menuOpen && (
          <div style={{
            width: '170px',
            minWidth: '170px',
            background: 'white',
            borderRight: '1px solid #e5e7eb',
            overflowY: 'auto',
            flexShrink: 0
          }}>
            {categories.map(category => {
              const CatIcon = category.icon;
              const isActiveCategory = category.id === activeCategoryId;
              const hasItems = category.items.length > 0;

              return (
                <div key={category.id}>
                  {/* Category Header */}
                  <div style={{
                    padding: '10px 12px 4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '11px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: isActiveCategory ? '#7c3aed' : '#9ca3af',
                    marginTop: category.id !== 'cat-general' ? '4px' : '0',
                    borderTop: category.id !== 'cat-general' ? '1px solid #f3f4f6' : 'none',
                    paddingTop: category.id !== 'cat-general' ? '10px' : '10px'
                  }}>
                    <CatIcon size={12} />
                    {category.label}
                  </div>

                  {/* Category Items */}
                  {hasItems ? category.items.map(item => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => setActiveTab(item.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          width: '100%',
                          padding: '8px 12px 8px 20px',
                          background: isActive ? '#f5f3ff' : 'transparent',
                          color: isActive ? '#7c3aed' : '#4b5563',
                          fontSize: '13px',
                          fontWeight: isActive ? '600' : '400',
                          borderLeft: isActive ? '3px solid #7c3aed' : '3px solid transparent',
                          textAlign: 'left',
                          transition: 'all 0.15s'
                        }}
                      >
                        <Icon size={14} />
                        {item.label}
                      </button>
                    );
                  }) : (
                    <div style={{
                      padding: '8px 12px 8px 20px',
                      fontSize: '12px',
                      color: '#c0c0c0',
                      fontStyle: 'italic'
                    }}>
                      Yakında...
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Content Area */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Admin;
